import { useCallback, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  getPostGetUsersQueryKey,
  usePostCreateUser,
  usePutUpdateUser,
} from "@/api/pizza-checker/users/users";
import type {
  PostCreateUserBody,
  PutUpdateUserBody,
} from "@/api/pizza-checker/schemas";
import { describeApiError } from "./user-utils";

type Messages = {
  success: string;
  error: string;
};

/**
 * Runs a user mutation the way every control on the Users page needs it:
 * toast the outcome (with the reason the API gave), reload the user list and
 * stay busy until the list shows the new state.
 */
const useUserMutation = () => {
  const queryClient = useQueryClient();
  const [isSaving, setIsSaving] = useState(false);

  const run = useCallback(
    async (mutate: () => Promise<unknown>, messages: Messages) => {
      setIsSaving(true);
      try {
        await mutate();
        toast.success(messages.success);
        // every list query, whatever its paging parameters
        await queryClient.invalidateQueries({
          queryKey: [getPostGetUsersQueryKey({})[0]],
        });
        return true;
      } catch (error) {
        toast.error(describeApiError(error, messages.error));
        return false;
      } finally {
        setIsSaving(false);
      }
    },
    [queryClient],
  );

  return { run, isSaving };
};

export const useUpdateUser = () => {
  const { run, isSaving } = useUserMutation();
  const { mutateAsync } = usePutUpdateUser();

  const updateUser = useCallback(
    (userId: string, data: PutUpdateUserBody, messages: Messages) =>
      run(() => mutateAsync({ userId, data }), messages),
    [run, mutateAsync],
  );

  return { updateUser, isUpdating: isSaving };
};

export const useCreateUser = () => {
  const { run, isSaving } = useUserMutation();
  const { mutateAsync } = usePostCreateUser();

  const createUser = useCallback(
    (data: PostCreateUserBody, messages: Messages) =>
      run(() => mutateAsync({ data }), messages),
    [run, mutateAsync],
  );

  return { createUser, isCreating: isSaving };
};
